(ns practitest.frontend.forms3.run.steps
  (:require
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.components.card-view :as card-view]
    [practitest.frontend.components.grid.localstorage :as grid-storage]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.markdown3 :as markdown3]
    [practitest.frontend.components.overflow :as overflow]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.run-status :as run-status]
    [practitest.frontend.components2.spinners :as spinners]
    [practitest.frontend.components2.tooltip :as tooltip]
    [practitest.frontend.forms3.entity-form.collapsible-section :refer [collapsible-section]]
    [practitest.frontend.forms3.run.events :as run-events]
    [practitest.frontend.forms3.run.subs :as run-subs]
    [re-frame.core :as re-frame]
    [reagent.core :as reagent]))

(def ^:private step-status-options
  [{:value "PASSED"  :label (get-translation "run-status.passed" "Passed")}
   {:value "FAILED"  :label (get-translation "step-status.fail" "Fail")}
   {:value "BLOCKED" :label (get-translation "step-status.block" "Block")}
   {:value "N/A"     :label (get-translation "run-status.na" "N/A")}
   {:value "NO RUN"  :label (get-translation "run-status.no-run" "No Run")}])

(defn- preconditions-row [preconditions expanded? on-toggle]
  (when (seq preconditions)
    [collapsible-section
     {:override-collapsed? (not expanded?)
      :class               "run-step-row"
      :title               [:span.run-step-row-title
                            (get-translation "test.step.preconditions-label" "Preconditions")]
      :on-toggle           (fn [_] (on-toggle))}
     [:div.run-step-row-body
      [:div.run-step-field-value preconditions]]]))

(defn- step-status-buttons [step-id run-id current-status edit-permissions?]
  [:div.run-step-status-buttons
   (doall
     (for [{:keys [value label]} step-status-options]
       (let [active? (= current-status value)
             color   (run-status/get-status-color value)]
         ^{:key value}
         [:button.run-step-status-btn
          {:class    (when active? "run-step-status-btn--active")
           :style    (when active?
                       {:background-color (str "color-mix(in srgb, " color " 15%, white)")
                        :color            color})
           :disabled (not edit-permissions?)
           :on-click (fn [e]
                       (.stopPropagation e)
                       (when (and edit-permissions? (not active?))
                         (mixpanel/track "Run Form Actions" {"Action" (str "Step Status Set " label)})
                         (re-frame/dispatch [::run-events/update-step-status step-id run-id value])
                         (let [next-row (some-> e .-target
                                                (.closest ".run-step-row")
                                                .-nextElementSibling)]
                           (when next-row
                             (.scrollIntoView next-row #js {:behavior "smooth" :block "nearest"})))))}
          label])))])

(defn- step-actions-menu [{:keys [id run-id expanded? on-toggle
                                   draft-name draft-desc draft-expected
                                   name description expected-results]}]
  (reagent/with-let [show-dropdown?       (reagent/atom false)
                     show-confirmation?   (reagent/atom false)
                     confirmation-details (reagent/atom nil)]
    (let [hide!               #(reset! show-dropdown? false)
          show-confirmation!  (fn [details]
                                (reset! confirmation-details details)
                                (reset! show-confirmation? true))
          hide-confirmation!  (fn [] (reset! show-confirmation? false))
          menu-item-callbacks {:hide!              hide!
                               :show-confirmation! show-confirmation!
                               :hide-confirmation! hide-confirmation!}]
      [:<>
       [card-view/confirmation-dialog show-confirmation? confirmation-details]
       [card-view/dropdown-menu
        {:show?      show-dropdown?
         :toggle!    (fn [e]
                       (.stopPropagation e)
                       (when-not @show-dropdown?
                         (mixpanel/track "Run Form Actions" {"Action" "Step Menu Opened"}))
                       (swap! show-dropdown? not))
         :hide!      hide!
         :class      "run-step-row-menu"
         :menu-items [[card-view/menu-item
                       menu-item-callbacks
                       {:label    (get-translation "run.steps.edit-step" "Edit Step")
                        :icon     icons/mux-pencil
                        :on-click (fn []
                                    (mixpanel/track "Run Form Actions" {"Action" "Step Edit Opened"})
                                    (when-not expanded? (on-toggle))
                                    (reset! draft-name (or name ""))
                                    (reset! draft-desc (or description ""))
                                    (reset! draft-expected (or expected-results ""))
                                    (re-frame/dispatch [::run-events/set-step-editing id]))}]
                      [card-view/menu-item
                       menu-item-callbacks
                       {:label    (get-translation "run.steps.add-step-above" "Add Step above")
                        :icon     icons/mux-add-step
                        :on-click (fn []
                                    (mixpanel/track "Run Form Actions" {"Action" "Step Added Above"})
                                    (re-frame/dispatch [::run-events/add-step id run-id true]))}]
                      [card-view/menu-item
                       menu-item-callbacks
                       {:label    (get-translation "run.steps.add-step-below" "Add Step below")
                        :icon     icons/mux-add-step
                        :on-click (fn []
                                    (mixpanel/track "Run Form Actions" {"Action" "Step Added Below"})
                                    (re-frame/dispatch [::run-events/add-step id run-id false]))}]
                      [card-view/menu-item
                       menu-item-callbacks
                       {:label        (get-translation "run.steps.delete-step" "Delete Step")
                        :icon         icons/mux-trash
                        :on-click     (fn []
                                        (mixpanel/track "Run Form Actions" {"Action" "Step Deleted"})
                                        (re-frame/dispatch [::run-events/delete-step id run-id]))
                        :confirmation {:title     (get-translation "run.steps.delete-step" "Delete Step")
                                       :body      (get-translation "run.steps.delete-step-confirm" "Are you sure you want to delete this step?")
                                       :yes-label (get-translation "generic.button.delete" "Delete")
                                       :no-label  (get-translation "generic.button.cancel" "Cancel")}}]]}]])))

(defn- step-field [{:keys [label value draft read-only?]}]
  [:div {:class (str "run-step-field" (when read-only? " run-step-field--readonly"))}
   [:span.run-step-field-label label]
   [markdown3/markdown-editor
    (if read-only?
      {:value value :read-only? true}
      {:value @draft :on-change #(reset! draft %)})]])

(defn- step-body [{:keys [editing? draft-desc draft-expected
                           description expected-results actual-results
                           edit-permissions? id cancel! save! saving?]}]
  (let [read-only? (not editing?)
        desc-field [step-field {:label      (get-translation "step.description" "Description")
                                :value      description
                                :draft      draft-desc
                                :read-only? read-only?}]
        exp-field  [step-field {:label      (get-translation "step.expected-results" "Expected Results")
                                :value      expected-results
                                :draft      draft-expected
                                :read-only? read-only?}]
        act-field  [:div.run-step-field
                    [:span.run-step-field-label (get-translation "step.actual-results" "Actual Results")]
                    [markdown3/markdown-editor
                     {:value      (or actual-results "")
                      :read-only? (not edit-permissions?)
                      :on-change  (fn [v] (re-frame/dispatch [::run-events/update-step-actual-results id v]))
                      :on-blur    (fn [_] (mixpanel/track "Run Form Actions" {"Action" "Actual Results Typed"}))}]]]
    (if editing?
      [:div.run-step-row-body--editing
       [:div.run-step-row-body desc-field exp-field act-field]
       [:div.run-step-edit-actions
        [buttons/button {:text      (get-translation "generic.button.cancel" "Cancel")
                         :disabled? saving?
                         :on-click  cancel!}]
        [buttons/button {:primary?  true
                         :disabled? saving?
                         :icon      (when saving? [spinners/mux-spinner {:size :small}])
                         :text      (get-translation "generic.button.save" "Save")
                         :on-click  save!}]]]
      [:div.run-step-row-body desc-field exp-field act-field])))

(defn- step-row [{:keys [id attributes]} expanded? on-toggle run-id edit-permissions?]
  (reagent/with-let [draft-name    (reagent/atom "")
                     draft-desc    (reagent/atom "")
                     draft-expected (reagent/atom "")]
    (let [{:keys [position name description expected-results actual-results status call-to-test-id]} attributes
          pending-edit-id @(re-frame/subscribe [::run-subs/pending-edit-step-id])
          saving?         @(re-frame/subscribe [::run-subs/step-saving? id])
          being-edited?   @(re-frame/subscribe [::run-subs/step-being-edited? id])
          ; auto-enter edit mode when this step was just created via add-step
          _               (when (and (= id pending-edit-id) (not being-edited?))
                            (when-not expanded? (on-toggle))
                            (re-frame/dispatch [::run-events/clear-pending-edit-step])
                            (reset! draft-name (or name ""))
                            (reset! draft-desc (or description ""))
                            (reset! draft-expected (or expected-results ""))
                            (re-frame/dispatch [::run-events/set-step-editing id]))
          cancel!         (fn []
                            (re-frame/dispatch [::run-events/clear-step-editing id]))
          save!           (fn []
                            (re-frame/dispatch [::run-events/update-step
                                               id
                                               {:name             @draft-name
                                                :description      @draft-desc
                                                :expected_results @draft-expected}]))]
      [collapsible-section
       {:override-collapsed? (not expanded?)
        :class               "run-step-row"
        :title               [:<>
                              (when call-to-test-id
                                [tooltip/tooltip
                                 {:placement "top"}
                                 (fn [hover-props]
                                   [:span.run-step-called-icon hover-props
                                    [icons/mux-folders]])
                                 (get-translation "run.steps.called-test" "Called Test")])
                              [:span.run-step-row-position (str "Step " position " - ")]
                              (if being-edited?
                                [:input.run-step-name-input
                                 {:value     @draft-name
                                  :on-change #(reset! draft-name (.. % -target -value))
                                  :on-click  #(.stopPropagation %)
                                  :auto-focus true}]
                                [overflow/overflow-with-mux-tooltip
                                 {}
                                 (fn [props]
                                   [:span.run-step-row-title props name])
                                 name])]
        :header-actions      [:<>
                              [step-status-buttons id run-id status edit-permissions?]
                              (when edit-permissions?
                                [step-actions-menu
                                 {:id               id
                                  :run-id           run-id
                                  :expanded?        expanded?
                                  :on-toggle        on-toggle
                                  :draft-name       draft-name
                                  :draft-desc       draft-desc
                                  :draft-expected   draft-expected
                                  :name             name
                                  :description      description
                                  :expected-results expected-results}])]
        :on-toggle           (fn [collapsed?]
                               (mixpanel/track "Run Form Actions" {"Action" (if collapsed? "Step Collapsed" "Step Expanded")})
                               (on-toggle))}
       [step-body {:editing?          being-edited?
                   :draft-desc        draft-desc
                   :draft-expected    draft-expected
                   :description       description
                   :expected-results  expected-results
                   :actual-results    actual-results
                   :edit-permissions? edit-permissions?
                   :id                id
                   :cancel!           cancel!
                   :save!             save!
                   :saving?           saving?}]])
    (finally
      (when @(re-frame/subscribe [::run-subs/step-being-edited? id])
        (re-frame/dispatch [::run-events/clear-step-editing id])))))

(defn run-steps-section [form-props]
  (reagent/with-let [section-starts-expanded? (not (grid-storage/get-section-collapsed
                                                      (:entity-type form-props)
                                                      "run-steps-section"
                                                      false))
                     expanded-steps           (reagent/atom #{})
                     initialized?             (reagent/atom false)]
    (let [{:keys [entity-type base-entity edit-permissions?]} form-props
          run-id        (:id base-entity)
          loading?      @(re-frame/subscribe [::run-subs/steps-loading?])
          preconditions @(re-frame/subscribe [::run-subs/preconditions])
          step-runs     @(re-frame/subscribe [::run-subs/step-runs])
          dirty?        @(re-frame/subscribe [::run-subs/steps-dirty?])
          all-ids       (cond-> (set (map :id step-runs))
                          (seq preconditions) (conj :precondition))]
      (when (and section-starts-expanded? (not loading?) (not @initialized?) (seq all-ids))
        (reset! initialized? true)
        (reset! expanded-steps all-ids))
      [collapsible-section
       {:entity-type    entity-type
        :storage-key    "run-steps-section"
        :hide-content?  false
        :class          "run-details-panel"
        :title          (get-translation "run.steps.title" "Run Steps")
        :on-toggle      (fn [collapsed?]
                          (mixpanel/track "Run Form Actions"
                            {"Action" (str "Run Steps" (if collapsed? " Collapsed" " Expanded"))})
                          (reset! expanded-steps (if collapsed? #{} all-ids)))
        :header-actions [:div.run-steps-header-actions
                         (when edit-permissions?
                           [:span.run-pass-all-btn
                            [buttons/button
                             {:primary?  true
                              :disabled? (not edit-permissions?)
                              :text      (get-translation "run.steps.pass-all" "Pass All")
                              :on-click  (fn [_]
                                           (mixpanel/track "Run Form Actions" {"Action" "Pass All Clicked"})
                                           (re-frame/dispatch [::run-events/pass-all run-id]))}]])
                         [buttons/icon-button
                          {:icon      (if dirty?
                                        [icons/mux-refresh-changes]
                                        [icons/mux-refresh-changes-disabled])
                           :disabled? (or (not edit-permissions?) (not dirty?))
                           :tooltip   (get-translation "run.steps.sync-to-test" "Update original test details")
                           :on-click  (fn [_]
                                        (mixpanel/track "Run Form Actions" {"Action" "Sync to Test Clicked"})
                                        (re-frame/dispatch [::run-events/sync-to-test run-id]))}]]}
       (if (and loading? (empty? step-runs))
         ;; Only show the full-list spinner on the initial load -
         ;; fetch-steps also runs after saving/adding/deleting any single step, and
         ;; swapping the whole list out for a spinner then would unmount every
         ;; step-row, discarding any other row's in-progress local edit state.
         [:div.run-steps-loading [spinners/mux-spinner {:size :small}]]
         [:<>
          [preconditions-row preconditions
           (contains? @expanded-steps :precondition)
           (fn [] (swap! expanded-steps
                         (fn [s] (if (contains? s :precondition)
                                   (disj s :precondition)
                                   (conj s :precondition)))))]
          [:div.run-steps-list
           (doall
             (for [step step-runs]
               (let [step-id (:id step)]
                 ^{:key step-id}
                 [step-row step
                  (contains? @expanded-steps step-id)
                  (fn [] (swap! expanded-steps
                                (fn [s] (if (contains? s step-id)
                                          (disj s step-id)
                                          (conj s step-id)))))
                  run-id
                  edit-permissions?])))]])])))
